<template>
  <div class="task-details flex flex-col gap-4">
    <div v-if="chip || isDone(task)" class="flex gap-1.5">
      <span v-if="chip" class="chip chip-auto">{{ chip }}</span>
      <span v-if="isDone(task)" class="chip chip-done">בוצעה</span>
    </div>
    <h2 class="m-0 text-xl font-extrabold leading-snug" :class="{ 'line-through text-surface-400': isDone(task) }">{{ task.title }}</h2>
    <div v-if="task.notes" class="bg-surface-50 rounded-[10px] p-3 text-sm text-surface-700 leading-relaxed whitespace-pre-line">{{ task.notes }}</div>
    <a
      v-if="task.actionUrl && !isDone(task)"
      :href="task.actionUrl"
      target="_blank"
      rel="noopener"
      class="h-11 rounded-[10px] bg-primary-50 text-primary-700 text-sm font-semibold no-underline flex items-center justify-center"
    >{{ task.actionLabel || 'פתיחה' }}</a>

    <div class="flex flex-col border border-surface-200 rounded-xl">
      <router-link
        v-if="task.customerId"
        :to="{ name: AdminRouteNames.ClientProfile, params: { id: task.customerId } }"
        class="detail-row no-underline"
      >
        <span class="flex-1 text-surface-500">לקוח</span>
        <span class="font-semibold" style="color: #0b5d65">{{ task.customerName }}</span>
        <i class="pi pi-chevron-left text-xs text-surface-400" />
      </router-link>
      <div class="detail-row">
        <span class="flex-1 text-surface-500">יעד</span>
        <span :class="isOverdue(task) ? 'text-red-700' : ''">{{ dueLabel(task) }}</span>
      </div>
      <div class="detail-row">
        <span class="flex-1 text-surface-500">באחריות</span>
        <span>{{ task.assigneeName || 'בעל העסק' }}</span>
      </div>
      <div v-if="recurrenceLabel(task)" class="detail-row">
        <span class="flex-1 text-surface-500">חזרה</span>
        <span>{{ recurrenceLabel(task) }}</span>
      </div>
      <div v-if="isDone(task) && task.completedAt" class="detail-row">
        <span class="flex-1 text-surface-500">בוצעה</span>
        <span>{{ completedLabel }}</span>
      </div>
    </div>

    <div class="flex gap-2">
      <Button
        :label="isDone(task) ? 'החזרה לפתוחות' : 'סימון כבוצע'"
        :icon="isDone(task) ? 'pi pi-replay' : 'pi pi-check'"
        class="flex-1 !h-11"
        :outlined="isDone(task)"
        :loading="busy"
        @click="emit('toggle', task)"
      />
      <Button label="עריכה" severity="secondary" outlined class="!h-11" @click="emit('edit', task)" />
    </div>
    <span v-if="closesItself" class="text-xs text-surface-500">{{ closesItself }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { type ITask, TaskAutoTypeEnum } from '@torim-app/types';
import { AdminRouteNames } from '@app/router/route-names';
import { dueLabel, isDone, isOverdue, recurrenceLabel, sourceChip } from './task-utils';

const props = defineProps<{ task: ITask; busy?: boolean }>();
const emit = defineEmits<{ toggle: [task: ITask]; edit: [task: ITask] }>();

const chip = computed(() => sourceChip(props.task));

const completedLabel = computed(() => {
  const at = props.task.completedAt ? DateTime.fromJSDate(new Date(props.task.completedAt)).setZone('Asia/Jerusalem').toFormat('d.M · HH:mm') : '';
  return props.task.completedByName ? `${at} · ${props.task.completedByName}` : at;
});

const CLOSES_ITSELF: Partial<Record<TaskAutoTypeEnum, string>> = {
  [TaskAutoTypeEnum.BotHandoff]: 'נסגרת לבד כשעונים ללקוח מאפליקציית WhatsApp Business',
  [TaskAutoTypeEnum.CustomerDebt]: 'נסגרת לבד כשהחוב מוסדר',
  [TaskAutoTypeEnum.MissingConsentForm]: 'נסגרת לבד כשהלקוח חותם על הטופס',
};
const closesItself = computed(() => (props.task.autoType && !isDone(props.task) ? CLOSES_ITSELF[props.task.autoType] : undefined));
</script>

<style scoped lang="scss">
.detail-row {
  @apply min-h-[46px] px-3.5 flex items-center gap-2 border-b border-surface-100 text-sm text-surface-900;

  &:last-child {
    @apply border-b-0;
  }
}

.chip {
  @apply text-[11px] font-semibold rounded-full px-2 py-0.5;
}

.chip-auto {
  background: #ffedd5;
  color: #9a3412;
}

.chip-done {
  @apply bg-primary-50 text-primary-700;
}
</style>
